"use client";

import Link from "next/link";
import { RoundedImage, Separator, Tags, ScrollContainer } from "@/components";
import { MdAccessTime } from "react-icons/md";
import clsx from "clsx";
import { Job } from "@/types";
import { formatCurrency, timeAgo } from "@/lib/utils";

interface OpportunityProps {
  job: Job;
  className?: string;
  redirectOnApply?: string | null;
}

const ApplyButton = ({ href }: { href?: string | null }) => {
  const button = (
    <button className="w-[93px] lg:w-[102px] h-[22px] lg:h-[24px] border border-brand-500 rounded-[5px] hover:bg-brand-500">
      <p className="text-brand-500 text-[12px] font-[600] hover:text-white">
        Apply
      </p>
    </button>
  );

  return href ? <Link href={href}>{button}</Link> : button;
};

export const Opportunity = ({
  job,
  className,
  redirectOnApply,
}: OpportunityProps) => {
  return (
    <div
      className={clsx(
        `
          w-[326.83px] h-[215.205px] 
          lg:w-[359.74px] lg:h-[236.875px]
          flex-shrink-0 
          rounded-[10px] bg-white 
          shadow-[0px_0px_8px_0px_rgba(0,0,0,0.15)]
          pt-[18px] lg:pt-[20px] px-[10px]
        `,
        className
      )}
    >
      <div className="pl-[14px] lg:pl-[15px]">
        <div className="flex flex-row pb-[11.5px]">
          <RoundedImage
            src={job.pictureCompany}
            className="aspect[1] w-[32px] lg:w-[35px] h-[32px] lg:h-[35px] mr-[10.5px]"
          />
          <div className="flex flex-col">
            <p className="text-[14px] font-[600] line-clamp-1">{job.title}</p>
            <p className="text-[10px] lg:text-[12px] font-[500] text-[#8A8A8A] mt-[-4px]">
              {job.comercialName}
            </p>
          </div>
        </div>
        <Tags
          tags={[
            job.workMode ?? "",
            job.jobLocation ?? "",
            job.experienceLevel ?? "",
          ]}
        />
        <ScrollContainer className="h-[45px] lg:h-[50px]">
          <p className="pt-[11px] lg:pt-[12px] text-[#8A8A8A] text-[10px] lg:text-[12px] font-[400] pr-[14px]">
            {job.description}
          </p>
        </ScrollContainer>
        <div className="pt-[14.5px] lg:pt-15.5px] flex flex-row space-x-[5.5px]">
          <MdAccessTime className="w-[14px] lg:w-[15.5px] aspect-[1] text-[#8A8A8A]" />
          <p className="text-[#8A8A8A] text-[10px] lg:text-[12px] font-[400]">
            {timeAgo(job.createdAt)}
          </p>
        </div>
      </div>
      <Separator className="mt-[10px] lg:mt-[12px] mb-[12px] lg:mb-[13px]" />
      <div className="flex flex-row justify-between items-center pl-[14.5px] pr-[13.5px]">
        <p className="text-[#8A8A8A] text-[12px] lg:text-[16px] font-[500]">
          <span className="text-brand-500 font-[600]">
            {formatCurrency(job.minMonthlyBudget, { currencySymbol: "$" })}
          </span>
          /m <span className="font-[400]">-</span>{" "}
          <span className="text-brand-500 font-[600]">
            {formatCurrency(job.maxMonthlyBudget, { currencySymbol: "$" })}
          </span>
          /m
        </p>
        <ApplyButton href={redirectOnApply} />
      </div>
    </div>
  );
};
